import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Building2, Briefcase, Users, Coins, Plus, Eye, Edit, Trash2,
  Calendar, MapPin, TrendingUp, Clock, CheckCircle, AlertCircle
} from 'lucide-react';
import MarkFulfilledModal from '../../components/MarkFulfilledModal';
import {
  DashboardShell,
  StatsCard,
  ApplicationsTab,
  PurchasedContactsTab,
  WalletTab,
} from '../../components/dashboard';
import apiClient from '../../services/apiClient';
import { toast } from 'sonner';

export const OrganizationDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ jobs: 0, approved: 0, pending: 0 });
  const [jobs, setJobs] = useState([]);
  const [wallet, setWallet] = useState({ balance: 0, transactions: [] });
  const [profile, setProfile] = useState(null);
  const [purchasedContacts, setPurchasedContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [fulfilledModal, setFulfilledModal] = useState({ open: false, type: '', id: '', title: '' });

  useEffect(() => {
    if (user && user.role === 'organization') fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchJobs(), fetchWallet(), fetchProfile(), fetchPurchasedContacts(), fetchApplications()]);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchasedContacts = async () => {
    try { const data = await apiClient.get('/purchased-contacts/my?status=all'); setPurchasedContacts(data.purchases || []); } catch { }
  };

  const fetchApplications = async () => {
    try { const data = await apiClient.get('/applications/my'); setApplications(data.applications || []); } catch { }
  };

  const fetchJobs = async () => {
    try {
      const orgJobs = await apiClient.get('/jobs/my');
      const list = orgJobs || [];
      setJobs(list);
      setStats({
        jobs: list.length,
        approved: list.filter(j => j.review_status === 'approved').length,
        pending: list.filter(j => j.review_status === 'pending').length,
      });
    } catch (err) { console.error('Error fetching jobs:', err); }
  };

  const fetchWallet = async () => {
    try {
      const [bal, txs] = await Promise.all([
        apiClient.get('/wallet/balance'),
        apiClient.get('/wallet/transactions'),
      ]);
      setWallet({ balance: bal.balance || 0, transactions: txs.transactions || [] });
    } catch (err) { console.error('Error fetching wallet:', err); }
  };

  const fetchProfile = async () => {
    try {
      const orgProfile = await apiClient.get('/profiles/organization/me');
      setProfile({ ...orgProfile, profile_id: orgProfile.id || true });
    } catch (orgError) {
      if (orgError.response?.status === 404) {
        try {
          const userData = await apiClient.get('/auth/me');
          setProfile({ ...userData, profile_id: null });
        } catch { setProfile({ profile_id: null }); }
      } else {
        setProfile({ profile_id: null });
      }
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    try {
      await apiClient.delete(`/jobs/${jobId}`);
      toast.success('Job deleted successfully');
      fetchJobs();
    } catch { toast.error('Failed to delete job'); }
  };

  if (!user || user.role !== 'organization') return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <DashboardShell
      title="Organization Dashboard"
      subtitle={`Welcome back, ${user.email}`}
      icon={Building2}
      iconBg="bg-blue-600"
      onLogout={logout}
    >
      {/* Profile Alert */}
      {!profile?.profile_id && (
        <Alert className="mb-6 bg-yellow-50 border-yellow-200">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Complete your organization profile to start posting jobs.
            <Button onClick={() => navigate('/profile/create')} size="sm" className="ml-4">Create Profile</Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <StatsCard title="Total Jobs" value={stats.jobs} icon={Briefcase} iconColor="text-blue-600" />
        <StatsCard title="Approved" value={stats.approved} icon={CheckCircle} iconColor="text-green-600" />
        <StatsCard title="Pending Review" value={stats.pending} icon={Clock} iconColor="text-yellow-600" />
        <StatsCard title="Wallet Balance" value={`${wallet.balance} coins`} icon={Coins} iconColor="text-purple-600" />
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="jobs" className="space-y-6">
        <TabsList>
          <TabsTrigger value="jobs">My Jobs</TabsTrigger>
          <TabsTrigger value="applications">Applications ({applications.length})</TabsTrigger>
          <TabsTrigger value="purchased">Purchased ({purchasedContacts.length})</TabsTrigger>
          <TabsTrigger value="wallet">Wallet</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* Jobs Tab */}
        <TabsContent value="jobs">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Posted Jobs</CardTitle>
                  <CardDescription>Manage your job postings</CardDescription>
                </div>
                <Button onClick={() => navigate('/jobs/create')}>
                  <Plus className="h-4 w-4 mr-2" /> Post New Job
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {jobs.length === 0 ? (
                <div className="text-center py-12">
                  <Briefcase className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">No jobs posted yet</p>
                  <Button onClick={() => navigate('/jobs/create')}>
                    <Plus className="h-4 w-4 mr-2" /> Post Your First Job
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {jobs.map((job) => (
                    <div key={job.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${job.review_status === 'pending' ? 'border-l-4 border-l-yellow-400 bg-yellow-50/30' :
                        job.review_status === 'rejected' ? 'border-l-4 border-l-red-400 bg-red-50/30' : ''
                      }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                            <Badge
                              variant={job.review_status === 'approved' ? 'default' : 'secondary'}
                              className={
                                job.review_status === 'pending' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                                  job.review_status === 'rejected' ? 'bg-red-100 text-red-800 border-red-300' :
                                    job.review_status === 'approved' ? 'bg-green-100 text-green-800 border-green-300' : ''
                              }
                              data-testid={`job-status-${job.id}`}
                            >
                              {job.review_status === 'pending' ? 'Under Review' :
                                job.review_status === 'rejected' ? 'Rejected' : 'Approved'}
                            </Badge>
                            {job.unlock_price > 0 && (
                              <Badge variant="outline" className="flex items-center">
                                <Coins className="h-3 w-3 mr-1" />{job.unlock_price}
                              </Badge>
                            )}
                          </div>
                          <div className="grid md:grid-cols-3 gap-4 mb-3 text-sm text-gray-600">
                            <div className="flex items-center"><MapPin className="h-4 w-4 mr-2" />{job.location}</div>
                            <div className="flex items-center"><Users className="h-4 w-4 mr-2" />{job.type}</div>
                            <div className="flex items-center"><Calendar className="h-4 w-4 mr-2" />Posted {job.posted_date}</div>
                          </div>
                          {job.subjects?.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {job.subjects.map((subject, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">{subject}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-2 ml-4">
                          {job.review_status === 'approved' && (
                            <Button size="sm" variant="outline" className="text-green-600 hover:text-green-700"
                              onClick={() => setFulfilledModal({ open: true, type: 'job', id: job.id, title: job.title })}>
                              <CheckCircle className="h-4 w-4 mr-1" /> Fulfilled
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => navigate(`/jobs/${job.id}/edit`)} data-testid={`edit-job-${job.id}`}>
                            <Edit className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => navigate(`/jobs/${job.id}`)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteJob(job.id)}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Shared Tabs */}
        <TabsContent value="applications"><ApplicationsTab applications={applications} /></TabsContent>
        <TabsContent value="purchased"><PurchasedContactsTab purchasedContacts={purchasedContacts} /></TabsContent>
        <TabsContent value="wallet"><WalletTab walletBalance={wallet.balance} walletTransactions={wallet.transactions} onNavigateWallet={() => navigate('/wallet')} /></TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2" /> Job Statistics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <span className="text-gray-700">Total Jobs Posted</span>
                    <span className="text-2xl font-bold text-blue-600">{stats.jobs}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <span className="text-gray-700">Approved & Live</span>
                    <span className="text-2xl font-bold text-green-600">{stats.approved}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
                    <span className="text-gray-700">Awaiting Review</span>
                    <span className="text-2xl font-bold text-yellow-600">{stats.pending}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Button onClick={() => navigate('/jobs/create')} className="w-full justify-start">
                    <Plus className="h-4 w-4 mr-2" /> Post New Job
                  </Button>
                  <Button onClick={() => navigate('/profile/create')} variant="outline" className="w-full justify-start">
                    <Building2 className="h-4 w-4 mr-2" /> Update Profile
                  </Button>
                  <Button onClick={() => navigate('/find-tutor')} variant="outline" className="w-full justify-start">
                    <Users className="h-4 w-4 mr-2" /> Browse Tutors
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Mark as Fulfilled Modal */}
      <MarkFulfilledModal
        isOpen={fulfilledModal.open}
        onClose={() => setFulfilledModal({ open: false, type: '', id: '', title: '' })}
        contentType={fulfilledModal.type}
        contentId={fulfilledModal.id}
        contentTitle={fulfilledModal.title}
        token={token}
        onSuccess={fetchJobs}
      />
    </DashboardShell>
  );
};
